/**
 * DateInputIT — Italian-style date input.
 *
 * Drop-in replacement for `<Input type="date">` with these features:
 *   • Visual format always gg/mm/aaaa (independent of browser locale).
 *   • Auto-inserts the "/" separators while the user types.
 *   • Validates the date silently — emits onChange with a YYYY-MM-DD ISO
 *     string (backend-compatible) ONLY when the value is a valid date.
 *   • Optional calendar popover icon for date-picking (shadcn calendar).
 *
 * API:
 *   <DateInputIT
 *      value={isoString}              // "YYYY-MM-DD" or "" — same as <input type="date">
 *      onChange={(iso) => ...}        // emits "YYYY-MM-DD" or ""
 *      min, max, disabled, className, data-testid
 *      showCalendar={true}            // default: true
 *   />
 */
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const isoToDisplay = (iso) => {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
};

const displayToIso = (display) => {
  if (!display) return "";
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(display);
  if (!m) return null;
  const dd = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  const yyyy = parseInt(m[3], 10);
  if (mm < 1 || mm > 12) return null;
  // Build a Date and verify roundtrip (catches invalid days like 31/02)
  const d = new Date(Date.UTC(yyyy, mm - 1, dd));
  if (d.getUTCFullYear() !== yyyy || d.getUTCMonth() !== mm - 1 || d.getUTCDate() !== dd) return null;
  return `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
};

const autoSlashes = (raw) => {
  // Strip non-digits, then re-insert slashes at positions 2 and 5.
  const digits = (raw || "").replace(/\D/g, "").slice(0, 8);
  let out = digits;
  if (digits.length >= 5) {
    out = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  } else if (digits.length >= 3) {
    out = `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }
  return out;
};

export const DateInputIT = forwardRef(function DateInputIT(
  {
    value,
    onChange,
    min,
    max,
    disabled = false,
    className,
    placeholder = "gg/mm/aaaa",
    showCalendar = true,
    ...rest
  },
  ref,
) {
  const [text, setText] = useState(() => isoToDisplay(value));
  const [open, setOpen] = useState(false);
  const lastIsoRef = useRef(value || "");

  // Keep local text in sync when parent value changes from outside
  useEffect(() => {
    if (value !== lastIsoRef.current) {
      setText(isoToDisplay(value));
      lastIsoRef.current = value || "";
    }
  }, [value]);

  const emit = (iso) => {
    lastIsoRef.current = iso || "";
    if (onChange) {
      // Mimic <input>-style event so existing handlers `(e) => e.target.value` still work
      onChange({ target: { value: iso || "" } });
    }
  };

  const handleType = (e) => {
    const formatted = autoSlashes(e.target.value);
    setText(formatted);
    const iso = displayToIso(formatted);
    if (iso === null) {
      // partial / invalid — clear ISO so parent doesn't see garbage
      if (lastIsoRef.current) emit("");
    } else {
      emit(iso);
    }
  };

  const handleBlur = () => {
    // On blur, if text is partial/invalid, clear to avoid lingering garbage.
    const iso = displayToIso(text);
    if (iso === null && text) setText("");
  };

  const selectedDate = useMemo(() => {
    if (!value) return undefined;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    return m ? new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10)) : undefined;
  }, [value]);

  return (
    <div className={cn("relative inline-flex items-center w-full", className)}>
      <input
        ref={ref}
        type="text"
        inputMode="numeric"
        placeholder={placeholder}
        value={text}
        onChange={handleType}
        onBlur={handleBlur}
        disabled={disabled}
        maxLength={10}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 file:border-0 file:bg-transparent file:text-sm file:font-medium",
          showCalendar && "pr-10",
        )}
        {...rest}
      />
      {showCalendar && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              tabIndex={-1}
              disabled={disabled}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-[#635F59] hover:text-[#AC5D45]"
              aria-label="Apri calendario"
            >
              <CalendarIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(d) => {
                if (!d) return;
                const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                setText(isoToDisplay(iso));
                emit(iso);
                setOpen(false);
              }}
              fromDate={min ? new Date(min) : undefined}
              toDate={max ? new Date(max) : undefined}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
});

export default DateInputIT;
